import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';

import { ContentPlacement } from '@candu-ai/placement/types';
import { useActiveTooltip } from 'craft/components/Tooltip';
import { TOOLBOX_CONTENT } from 'craft/containers/Toolbox/constants';
import { useTooltipPlacement } from 'craft/hooks';
import { tooltipTypeForNode } from 'craft/types/TooltipType';

import { StepOne, StepTwo } from './components';

enum PlacementStep {
  One = 'One',
  Two = 'Two',
}

interface HotspotPlacementsProps {
  placement: ContentPlacement;
  onEditUrl: VoidFunction;
  placementSaved: {
    values: ContentPlacement;
    id?: string;
  } | null;
  resetPlacementSaved: VoidFunction;
}

export const TooltipPlacements = ({
  placement,
  onEditUrl,
  placementSaved,
  resetPlacementSaved,
}: HotspotPlacementsProps) => {
  const activeTooltip = useActiveTooltip();
  const name = activeTooltip?.data?.name;
  const tooltipType = tooltipTypeForNode(name);

  const { placement: hotspotPlacement } = useTooltipPlacement(activeTooltip?.id);
  const { resetForm } = useFormikContext<ContentPlacement>();
  const [stepOneSelector, setStepOneSelector] = useState('');
  const [currentStep, setCurrentStep] = useState(
    hotspotPlacement?.selector ? PlacementStep.Two : PlacementStep.One,
  );

  useEffect(() => {
    // if we change hotspot we should reset
    resetForm();
    setStepOneSelector('');
    setCurrentStep(hotspotPlacement?.selector ? PlacementStep.Two : PlacementStep.One);
  }, [activeTooltip?.id, hotspotPlacement?.id]);

  useEffect(() => {
    // used to sync placement state between tab and edit placement modal
    if (placementSaved !== null) {
      // we need to also reset this form with the placement values
      resetForm(placementSaved);
      resetPlacementSaved();
    }
  }, [placementSaved, resetPlacementSaved]);

  // scroll to top on hotspot / step change
  useEffect(() => {
    document.getElementById(TOOLBOX_CONTENT)?.scrollTo(0, 0);
  }, [activeTooltip?.id, currentStep]);

  if (!activeTooltip || !tooltipType) {
    return null;
  }

  if (currentStep === PlacementStep.One) {
    return (
      <StepOne
        contentType={tooltipType}
        key={activeTooltip.id}
        initialValue={stepOneSelector || hotspotPlacement?.selector}
        skipShouldExit={!!hotspotPlacement?.selector && !stepOneSelector}
        handleSkip={() => {
          setCurrentStep(PlacementStep.Two);
        }}
        handleConfirmPlacement={(value: string) => {
          setStepOneSelector(value);
          setCurrentStep(PlacementStep.Two);
        }}
      />
    );
  }

  return (
    <StepTwo
      contentType={tooltipType}
      key={`${activeTooltip.id}-${stepOneSelector}`}
      placement={placement}
      onEditUrl={onEditUrl}
      stepOneSelector={stepOneSelector}
      onSave={() => {
        setStepOneSelector('');
      }}
      handleRetargetClick={() => {
        setCurrentStep(PlacementStep.One);
      }}
    />
  );
};
